import { type FC, useEffect, useState } from 'react';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useDownvoteReview } from '../../../bff-api/turn-to/useDownvoteReview';
import { useFlagReview } from '../../../bff-api/turn-to/useFlagReview';
import { useUpvoteReview } from '../../../bff-api/turn-to/useUpvoteReview';
import { usePDP } from '../../../contexts/pdp/usePDP';
import { Header, Rating, Text } from '../../../ui-components';
import { getAuthorName } from '../../../utils/contact-utils';
import { GetIcon } from '../../icons';
import { ResponsiveImage } from '../../responsive-image';
import { Gallery } from './Gallery';
import { TurnToTag } from './Tag';
import type { Photo, UserTypes } from '../../../bff-api/turn-to/model';
import type { Syndication } from '../../../bff-api/turn-to/model/Syndication';

export interface ReviewItemProps {
    title: string | null;
    text: string;
    rating: number;
    dateCreated: string;
    upVotes: number;
    downVotes: number;
    inappropriateFlag: boolean;
    id: number;
    photoList: Photo[];
    syndication?: Syndication;
    user: UserTypes;
}

export const ReviewItem: FC<ReviewItemProps> = ({
    title,
    text,
    rating,
    dateCreated,
    upVotes,
    downVotes,
    inappropriateFlag,
    id,
    photoList,
    syndication,
    user,
}) => {
    const date = new Intl.DateTimeFormat('default', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(new Date(dateCreated));
    const { product } = usePDP();

    const [localUpvotes, setLocalUpvotes] = useState<number>(0);
    const [localDownVotes, setLocalDownvotes] = useState<number>(0);

    const [selectedPhoto, setSelectedPhoto] = useState<Photo>();
    const [isLiked, setIsLiked] = useState<boolean>(false);
    const [isDisliked, setIsDisliked] = useState<boolean>(false);
    const [isFlagged, setIsFlagged] = useState<boolean>(false);

    const upvoteReviewRequest = useUpvoteReview(id);
    const downvoteReviewRequest = useDownvoteReview(id);
    const flagReviewRequest = useFlagReview(id);

    const { firstName, lastName, nickName } = user;

    useEffect(() => {
        if (inappropriateFlag) {
            setIsFlagged(true);
        }
    }, [inappropriateFlag]);

    useEffect(() => {
        setLocalUpvotes(upVotes);
        setLocalDownvotes(downVotes);
        setIsFlagged(inappropriateFlag);
    }, [upVotes, downVotes, inappropriateFlag]);

    const upvoteReview = async (): Promise<void> => {
        if (isLiked) {
            setIsLiked(false);
            setLocalUpvotes(localUpvotes - 1);
            return;
        }

        const result = await upvoteReviewRequest.mutateAsync([]);
        if (result) {
            setIsLiked(true);
            setLocalUpvotes(localUpvotes + 1);
            if (isDisliked) {
                setIsDisliked(false);
                setLocalDownvotes(localDownVotes - 1);
            }
        }
    };

    const downvoteReview = async (): Promise<void> => {
        if (isDisliked) {
            setIsDisliked(false);
            setLocalDownvotes(localDownVotes - 1);
            return;
        }
        const result = await downvoteReviewRequest.mutateAsync([]);
        if (result) {
            setIsDisliked(true);
            setIsLiked(false);

            setLocalDownvotes(localDownVotes + 1);
            if (isLiked) {
                setIsLiked(false);
                setLocalUpvotes(localUpvotes - 1);
            }
        }
    };

    const flagReview = async (): Promise<void> => {
        if (isFlagged) {
            return;
        }

        await flagReviewRequest.mutateAsync([]);
        setIsFlagged(true);
    };

    const {
        isOpen: isGalleryOpen,
        onOpen: onOpenGallery,
        onClose: onCloseGallery,
    } = useDisclosure();

    const openGallery = (photo: Photo): void => {
        setSelectedPhoto(photo);
        onOpenGallery();
    };

    const authorName = nickName ?? getAuthorName(firstName, lastName?.[0]);

    return (
        <Box
            paddingY="1rem"
            borderBottom="1px solid"
            _last={{ borderBottom: 'none' }}
            borderColor="slt-pepper.400"
        >
            <Flex flexWrap="wrap" gap={{ lg: '2rem' }} justifyContent="space-between">
                <Flex flexWrap={{ base: 'wrap', sm: 'nowrap' }} gap={{ lg: '2rem' }}>
                    <Box minW="304px">
                        <Box w="100px" h="100px" mb={4}>
                            {product?.imageGroups?.[0].images[0].link ? (
                                <ResponsiveImage
                                    id={`turn-to-product-image-${product?.id}`}
                                    src={product?.imageGroups?.[0].images[0].link}
                                    alt={product?.name}
                                    width="100px"
                                    height="100px"
                                    loading="lazy"
                                />
                            ) : null}
                        </Box>
                        <Rating initialRating={rating} disabled />
                        <Box mt={2}>
                            <Text color="slt-pepper.700" size="small">
                                {date}
                            </Text>
                        </Box>
                    </Box>
                    <Box width={{ base: '100%', lg: 'auto' }} mt={4}>
                        <Header as="h4" align="left" style="bold" variant="header4">
                            {title}
                        </Header>
                        <Box mt={2}>
                            <Text>{text}</Text>
                        </Box>
                        <Box display="flex" alignItems="center" className="space-x-2">
                            <Text size="small">{authorName}</Text>
                            {user?.badge ? <TurnToTag tagText={user.badge.text} /> : null}
                        </Box>
                        {syndication?.sourceName ? (
                            <Box>
                                <Text size="small">
                                    <FormattedMessage
                                        defaultMessage="Originally posted at {syndicationSourceName}"
                                        id="turn-to.syndication.source-name"
                                        values={{ syndicationSourceName: syndication.sourceName }}
                                    />
                                </Text>
                            </Box>
                        ) : null}
                    </Box>
                </Flex>
                {!!photoList.length && (
                    <Flex className="space-x-3" marginRight="70px" mt={4}>
                        {photoList.map((photo) => (
                            <Box
                                w="100px"
                                h="100px"
                                key={photo.id}
                                onClick={() => openGallery(photo)}
                                cursor="pointer"
                            >
                                <img
                                    width="100%"
                                    height="100%"
                                    src={photo.thumbnailUrl}
                                    alt={photo.caption}
                                    loading="lazy"
                                />
                            </Box>
                        ))}
                    </Flex>
                )}
            </Flex>
            <Box display="flex" justifyContent="space-between" mt={6}>
                <Box display="flex">
                    <Text size="small" margin="0 1rem 0 0">
                        <FormattedMessage
                            defaultMessage="Was this helpful?"
                            id="turn-to.was-it-helpful"
                        />
                    </Text>
                    <Flex
                        cursor="pointer"
                        onClick={upvoteReview}
                        marginRight={4}
                        alignItems="center"
                        as="button"
                    >
                        {isLiked ? (
                            <GetIcon name="like-filled" size="sm" />
                        ) : (
                            <GetIcon name="like" size="sm" />
                        )}
                        <Text size="small" margin="0 0 0 0.25rem">
                            {localUpvotes}
                        </Text>
                    </Flex>
                    <Flex cursor="pointer" onClick={downvoteReview} alignItems="center" as="button">
                        {isDisliked ? (
                            <GetIcon name="reverted-like-filled" size="sm" />
                        ) : (
                            <GetIcon name="reverted-like" size="sm" />
                        )}
                        <Text size="small" margin="0 0 0 0.25rem">
                            {localDownVotes}
                        </Text>
                    </Flex>
                </Box>
                <Box display="flex" onClick={flagReview} as="button">
                    {isFlagged ? (
                        <GetIcon name="flag-filled" size="sm" />
                    ) : (
                        <GetIcon name="flag" size="sm" />
                    )}
                    <Text margin="0 0 0 0.25rem" size="small">
                        {isFlagged ? (
                            <FormattedMessage defaultMessage="Flagged" id="turn-to.flagged" />
                        ) : (
                            <FormattedMessage defaultMessage="Flag" id="turn-to.flag" />
                        )}
                    </Text>
                </Box>
            </Box>
            {isGalleryOpen && (
                <Gallery
                    isOpen={isGalleryOpen}
                    onClose={onCloseGallery}
                    selectedPhoto={selectedPhoto}
                    rating={rating}
                    nickName={nickName || ''}
                    firstName={firstName}
                    lastName={lastName || ''}
                    text={text}
                    photoList={photoList}
                />
            )}
        </Box>
    );
};
